// src/components/AdminPanel/Dashboard.js
import React, { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import CandidateTable from "./CandidateTable";

const Dashboard = () => {
  const [candidates, setCandidates] = useState({ upcoming: [], completed: [] });
  const [activeCategory, setActiveCategory] = useState("upcoming");
  const [certificationsCount, setCertificationsCount] = useState(0);

  // ✅ Fetch candidates from Firestore
  useEffect(() => {
    const q = query(collection(db, "jobApplications"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allCandidates = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const upcoming = allCandidates.filter(
        (c) =>
          !c.status ||
          c.status.toLowerCase() === "pending" ||
          c.status.toLowerCase() === "interview scheduled"
      );

      const completed = allCandidates.filter(
        (c) =>
          c.status &&
          (c.status.toLowerCase() === "selected" ||
            c.status.toLowerCase() === "completed")
      );

      setCandidates({ upcoming, completed });
    });

    return () => unsubscribe();
  }, []);

  // ✅ Fetch certifications count
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "certifications"), (snapshot) => {
      setCertificationsCount(snapshot.size);
    });
    return () => unsubscribe();
  }, []);

  // Common gradient for all cards
  const cardGradient = "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)";
  // Slightly lighter gradient for first card (selected/highlighted)
  const highlightedGradient = "linear-gradient(135deg, #FF9F2E 0%, #FF8C00 100%)";

  return (
    <div className="dashboard-page">
      <h1 className="page-heading">
        <i className="fas fa-home" style={{ color: "#FF8C00" }}></i> Admin Dashboard
      </h1>

      {/* Stats Cards */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "30px", flexWrap: "wrap" }}>
        {/* Total Candidates Card - Highlighted */}
        <div style={{ 
          background: highlightedGradient,
          padding: "24px",
          borderRadius: "16px",
          color: "white",
          minWidth: "200px",
          boxShadow: "0 8px 20px rgba(255, 140, 0, 0.3)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          transform: "translateY(-2px)",
          transition: "all 0.3s ease"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-users" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Total Candidates</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {candidates.upcoming.length + candidates.completed.length}
          </p>
        </div>

        {/* Upcoming Interviews Card */}
        <div style={{ 
          background: cardGradient,
          padding: "24px",
          borderRadius: "16px",
          color: "white",
          minWidth: "200px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          transition: "all 0.3s ease"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-clock" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Upcoming</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {candidates.upcoming.length}
          </p>
        </div>

        {/* Completed Card */}
        <div style={{ 
          background: cardGradient,
          padding: "24px",
          borderRadius: "16px",
          color: "white",
          minWidth: "200px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          transition: "all 0.3s ease"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-check-circle" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Completed</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {candidates.completed.length}
          </p>
        </div>

        {/* Certifications Card */}
        <div style={{ 
          background: cardGradient,
          padding: "24px",
          borderRadius: "16px",
          color: "white",
          minWidth: "200px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          transition: "all 0.3s ease"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-certificate" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Certifications</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {certificationsCount}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="category-tabs" style={{ 
        display: "flex", 
        gap: "0", 
        borderBottom: "1px solid #E2E8F0",
        marginBottom: "20px"
      }}>
        <button
          className={activeCategory === "upcoming" ? "tab active" : "tab"}
          onClick={() => setActiveCategory("upcoming")}
          style={{
            padding: "12px 24px",
            background: activeCategory === "upcoming" ? "#FF8C00" : "transparent",
            color: activeCategory === "upcoming" ? "white" : "#4A5568",
            border: "none",
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            transition: "all 0.2s",
            marginRight: "4px"
          }}
        >
          Upcoming
        </button>
        <button
          className={activeCategory === "completed" ? "tab active" : "tab"}
          onClick={() => setActiveCategory("completed")}
          style={{
            padding: "12px 24px",
            background: activeCategory === "completed" ? "#FF8C00" : "transparent",
            color: activeCategory === "completed" ? "white" : "#4A5568",
            border: "none",
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            transition: "all 0.2s"
          }}
        >
          Completed
        </button>
      </div>

      {/* Candidate Table */}
      <CandidateTable
        candidates={candidates}
        setCandidates={setCandidates}
        activeCategory={activeCategory}
      />
    </div>
  );
};

export default Dashboard;